/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { adminApi } from '../../lib/adminApi';
import { Users, RefreshCw, Globe, Pencil, Lock, ShieldCheck } from 'lucide-react';

const fmtTime = (t) => {
  if (!t) return '—';
  try {
    return new Date(t * 1000).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' });
  } catch { return '—'; }
};

const shortSession = (sid) => (sid || '').slice(0, 10);

const SiteMonitor = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem('site_monitor_unlocked') === '1');
  const [pw, setPw] = useState('');
  const [pwError, setPwError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const handleUnlock = async (e) => {
    e?.preventDefault();
    setPwError('');
    if (!pw) { setPwError('Please enter the password.'); return; }
    setVerifying(true);
    try {
      await adminApi.post('/monitor/unlock', { password: pw });
      sessionStorage.setItem('site_monitor_unlocked', '1');
      setUnlocked(true);
      setPw('');
    } catch (err) {
      setPwError(err?.response?.data?.detail || 'Invalid password.');
    } finally {
      setVerifying(false);
    }
  };

  const handleLock = () => {
    sessionStorage.removeItem('site_monitor_unlocked');
    setUnlocked(false);
    setLogs([]);
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get('/visitor-logs?limit=200');
      setLogs(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!unlocked) return;
    load();
    const id = setInterval(load, 15000); // auto-refresh every 15s
    return () => clearInterval(id);
  }, [unlocked]);

  const parseUA = (ua) => {
    if (!ua) return { device: 'Unknown', browser: '' };
    const isMobile = /Mobile|Android|iPhone|iPad/i.test(ua);
    let browser = 'Other';
    if (/Chrome\//i.test(ua) && !/Edg|OPR/i.test(ua)) browser = 'Chrome';
    else if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari';
    else if (/Firefox\//i.test(ua)) browser = 'Firefox';
    else if (/Edg\//i.test(ua)) browser = 'Edge';
    return { device: isMobile ? 'Mobile' : 'Desktop', browser };
  };

  if (!unlocked) {
    return (
      <div className="max-w-md mx-auto mt-10" data-testid="site-monitor-lock">
        <Card className="border-[#1565C0]/30 shadow-lg">
          <CardHeader className="pb-3 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#1565C0]/10 flex items-center justify-center mb-3">
              <Lock className="w-7 h-7 text-[#1565C0]" />
            </div>
            <CardTitle className="text-xl">Site Monitor is Locked</CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              Enter the monitor password to view visitor logs.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUnlock} className="space-y-3">
              <Input
                type="password"
                placeholder="Enter password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoFocus
                data-testid="monitor-password-input"
              />
              {pwError && (
                <div className="text-xs text-red-600" data-testid="monitor-password-error">{pwError}</div>
              )}
              <Button
                type="submit"
                className="w-full bg-[#1565C0] hover:bg-[#0F4C9E]"
                disabled={verifying}
                data-testid="monitor-unlock-btn"
              >
                {verifying ? 'Verifying…' : (<><ShieldCheck className="w-4 h-4 mr-1" /> Unlock</>)}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="site-monitor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><Users className="w-6 h-6 text-[#1565C0]" /> Site Monitor · Visitor Log</h2>
          <p className="text-xs text-gray-500">Live view of who's browsing the site (auto-refreshes every 15 s).</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="monitor-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleLock} data-testid="monitor-lock-btn">
            <Lock className="w-4 h-4 mr-1" /> Lock
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox label="Total visits (all-time)" value={logs.length} />
        <StatBox label="Live now (< 60 s)" value={logs.filter(l => (Date.now() / 1000 - l.last_seen) < 60).length} tone="green" />
        <StatBox label="Mobile" value={logs.filter(l => parseUA(l.user_agent).device === 'Mobile').length} tone="blue" />
        <StatBox label="Desktop" value={logs.filter(l => parseUA(l.user_agent).device === 'Desktop').length} tone="blue" />
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Visitors ({logs.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs font-semibold uppercase tracking-widest text-gray-500">
                  <th className="px-4 py-3">Session</th>
                  <th className="px-4 py-3">IP Address</th>
                  <th className="px-4 py-3">Device</th>
                  <th className="px-4 py-3">Last Page</th>
                  <th className="px-4 py-3">First Seen</th>
                  <th className="px-4 py-3">Last Seen</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {logs.length === 0 && (
                  <tr><td colSpan="7" className="text-center text-gray-500 py-8">
                    {loading ? 'Loading…' : 'No visitors yet.'}
                  </td></tr>
                )}
                {logs.map((l) => {
                  const { device, browser } = parseUA(l.user_agent);
                  const isLive = (Date.now() / 1000 - l.last_seen) < 60;
                  return (
                    <tr key={l.session_id} className="hover:bg-gray-50" data-testid={`visitor-row-${l.session_id.slice(0,8)}`}>
                      <td className="px-4 py-3 font-mono text-xs">
                        {isLive && <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-2 animate-pulse" />}
                        {shortSession(l.session_id)}…
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{l.ip || '—'}</td>
                      <td className="px-4 py-3 text-xs">
                        <span className={`px-1.5 py-0.5 rounded ${device === 'Mobile' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>{device}</span>
                        <span className="ml-1 text-gray-500">{browser}</span>
                      </td>
                      <td className="px-4 py-3">
                        <a href={l.path} target="_blank" rel="noopener noreferrer" className="text-[#1565C0] hover:underline flex items-center gap-1">
                          <Globe className="w-3 h-3" />{l.path}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">{fmtTime(l.first_seen)}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{fmtTime(l.last_seen)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(l)} title="View / Edit" data-testid={`visitor-edit-${l.session_id.slice(0,8)}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {editing && (
        <Card className="border-[#1565C0]/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              Visitor Details
              <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Close</Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Row label="Session ID"><code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{editing.session_id}</code></Row>
              <Row label="IP Address">{editing.ip || '—'}</Row>
              <Row label="Last Page"><a href={editing.path} target="_blank" rel="noopener noreferrer" className="text-[#1565C0] hover:underline">{editing.path}</a></Row>
              <Row label="First Seen">{fmtTime(editing.first_seen)}</Row>
              <Row label="Last Seen">{fmtTime(editing.last_seen)}</Row>
              <Row label="Duration">{Math.max(0, Math.round((editing.last_seen - editing.first_seen))) + ' s'}</Row>
              <div className="md:col-span-2">
                <dt className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-1">User-Agent</dt>
                <dd className="font-mono text-xs bg-gray-50 border p-2 rounded break-all">{editing.user_agent || '—'}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const Row = ({ label, children }) => (
  <div>
    <dt className="text-xs font-semibold uppercase tracking-widest text-gray-500">{label}</dt>
    <dd className="text-sm text-[#0A1628] mt-0.5">{children}</dd>
  </div>
);

const StatBox = ({ label, value, tone = 'slate' }) => (
  <div className={`p-4 rounded-lg border shadow-sm bg-white`}>
    <div className={`text-2xl font-bold leading-none tabular-nums ${
      tone === 'green' ? 'text-emerald-600' :
      tone === 'blue' ? 'text-blue-600' : 'text-slate-700'
    }`}>{value}</div>
    <div className="text-xs text-gray-500 mt-1">{label}</div>
  </div>
);

export default SiteMonitor;
